import { db } from './firebase';
import { doc, onSnapshot, setDoc, updateDoc, FieldPath } from 'firebase/firestore';
import { architectDiscussionReceiptKey } from './commentAudience';
import { markGroupNotificationsAsRead, markTaskNotificationsAsRead } from './notificationReadSync';

const COLLECTION = 'taskReadReceipts';

export type ReadReceiptMap = Record<string, number>;

/** Normalize task `lastCommentAt` from Firestore / cache into epoch ms, or null if unusable. */
function commentTimestampToMs(value: unknown): number | null {
  if (value == null) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (typeof o.toDate === 'function') {
      try {
        const d = (o.toDate as () => Date)();
        if (!(d instanceof Date)) return null;
        const t = d.getTime();
        return Number.isNaN(t) ? null : t;
      } catch {
        return null;
      }
    }
    if (typeof o.seconds === 'number') {
      const nano = typeof o.nanoseconds === 'number' ? o.nanoseconds : 0;
      return o.seconds * 1000 + nano / 1e6;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/**
 * Subscribe to a user's task read receipts.
 * Document: taskReadReceipts/{userId} -> { receipts: { [taskId]: timestampMs } }
 */
export function subscribeToReadReceipts(
  userId: string,
  callback: (receipts: ReadReceiptMap) => void
): () => void {
  const ref = doc(db, COLLECTION, userId);
  return onSnapshot(ref, snap => {
    const data = snap.data();
    callback((data?.receipts as ReadReceiptMap) || {});
  }, () => {
    callback({});
  });
}

/**
 * Set one receipt without replacing the rest of the map.
 * `setDoc(..., { merge: true })` overwrites a nested `receipts` object, so opening
 * Design decisions (a second key) was wiping the group-thread receipt and the
 * unread dot never cleared.
 */
async function writeReceiptKey(userId: string, receiptKey: string): Promise<void> {
  if (!userId || !receiptKey) return;
  const ref = doc(db, COLLECTION, userId);
  const value = Date.now();
  try {
    await updateDoc(ref, new FieldPath('receipts', receiptKey), value);
  } catch (err) {
    const code = (err as { code?: string }).code;
    if (code !== 'not-found') throw err;
    await setDoc(ref, { receipts: { [receiptKey]: value } });
  }
}

/**
 * Mark a task as read for the current user (sets timestamp to now).
 */
export async function markTaskAsRead(userId: string, taskId: string): Promise<void> {
  await writeReceiptKey(userId, taskId);
  void markTaskNotificationsAsRead(userId, taskId);
}

/**
 * Mark a consolidated group comment thread as read for the current user.
 * Read receipts share the same `taskReadReceipts/{userId}.receipts` map, keyed by the group key.
 */
export async function markGroupAsRead(userId: string, groupKey: string): Promise<void> {
  await writeReceiptKey(userId, groupKey);
  void markGroupNotificationsAsRead(userId, groupKey);
}

/** Marks Architect Discussion as seen without treating the internal Comments tab as read. */
export async function markArchitectDiscussionAsRead(userId: string, groupKey: string): Promise<void> {
  await writeReceiptKey(userId, architectDiscussionReceiptKey(groupKey));
}

/**
 * Mark a project's file comments as seen (receipt key `files:{projectId}`) —
 * the cursor for the rollup-driven landing "new file comments" dot.
 */
export async function markProjectFilesSeen(userId: string, projectId: string): Promise<void> {
  await writeReceiptKey(userId, `files:${projectId}`);
}

/**
 * Check if a task has unread comments for a given user.
 */
export function hasUnreadComments(
  task: { id?: string; lastCommentAt?: unknown; lastCommentBy?: string },
  userId: string,
  receipts: ReadReceiptMap
): boolean {
  if (!task.id || task.lastCommentAt == null) return false;
  if (task.lastCommentBy === userId) return false;
  const readAt = receipts[task.id] || 0;
  const commentAt = commentTimestampToMs(task.lastCommentAt);
  if (commentAt == null) return false;
  return commentAt > readAt;
}
